<template>
    <div class="mt-5 step-3">
        <div class="summary mb-6" v-if="groupedClaims">
            <div
                v-for="(luminaires, salesOrderNumber) in groupedClaims"
                :key="salesOrderNumber"
                class="summary_group mb-3 mt-6"
            >
                <!-- Main attributes (once per sales order) -->
                <div class="main-attributes flex-column align-start mb-3">
                    <div class="flex-between">
                        <div class="bold">{{ 'Sales order number' }}</div>
                        <div class="">{{ salesOrderNumber }}</div>
                    </div>
                    <div class="flex-between">
                        <div class="bold">{{ 'Customer order number' }}</div>
                        <div class="">{{ luminaires[0].customer_order_number }}</div>
                    </div>
                    <div class="flex-between">
                        <div class="bold">{{ 'Customer' }}</div>
                        <div class=""> {{ luminaires[0].customer }}</div>
                    </div>
                </div>
                <!-- Secondary attributes (per luminaire) -->
                <vue-good-table
                    :columns="luminaireColumns"
                    :rows="luminaires"
                >
                    <template slot="table-row" slot-scope="props">
                        <span v-if="props.column.field !== 'issues'">
                            {{ formatValue(props.formattedRow[props.column.field]) }}
                        </span>
                        <div v-else @click="openIssuesModal(props.row)" class="pointer">
                            <svg-image
                                class="icon"
                                icon="list"
                                height="16"
                                width="16"
                            />
                        </div>
                    </template>
                </vue-good-table>
            </div>
        </div>
        <form class="form order-attributes" enctype="multipart/form-data">
            <div class="flex-column half-width align-start">
                <div class="form-group">
                    <ValidationProvider
                        v-slot="{ errors }"
                        rules="required|min:3"
                    >
                        <SfInput
                            type="text_field"
                            :valid="!errors[0]"
                            :error-message="$t(errors[0])"
                            v-model="applicantDetails.fullName"
                            name="fullName"
                            required
                            :label="$t('Full name')"
                            class="form__element"
                            autocomplete="off"
                            @blur="saveApplicantDetails()"
                        />
                    </ValidationProvider>
                </div>
                <div class="form-group">
                    <ValidationProvider
                        v-slot="{ errors }"
                        rules="required|email"
                        class="form__element"
                    >
                        <SfInput
                            v-model="applicantDetails.email"
                            type="email"
                            name="email"
                            :label="$t('Email address')"
                            required
                            :valid="!errors[0]"
                            :error-message="$t(errors[0])"
                            @blur="saveApplicantDetails()"
                        />
                    </ValidationProvider>
                </div>
                <div class="form-group">
                    <ValidationProvider
                        v-slot="{ errors }"
                        rules="required|min:3"
                        class="form__element"
                    >
                        <SfInput
                            v-model="applicantDetails.telephone"
                            name="telephone"
                            :label="$t('Phone number')"
                            required
                            :valid="!errors[0]"
                            :error-message="$t(errors[0])"
                            @blur="saveApplicantDetails()"
                        />
                    </ValidationProvider>
                </div>
            </div>
        </form>
        <div v-if="showFinalStepWarning" class="warning-message">{{ $t('Please fill in all required fields') }}</div>
        <SfModal
            :visible="isModalOpen"
            class="project-modal container"
            @close="isModalOpen = false; activeIssues.value = []"
        >
            <div>
                <h4>{{ $t('Product issues') }}</h4>
            </div>
            <div class="mb-4">
                <vue-good-table
                ref="issueList"
                :columns="issueColumnsData"
                :rows="activeIssues"
            >
                <template slot="table-column" slot-scope="props">
                    <span>
                        {{ props.column.label }}
                    </span>
                </template>
                <template slot="table-row" slot-scope="props">
                    <span>
                        {{
                            isDateField(props.column.field)
                                ? formatDate(props.formattedRow[props.column.field])
                                : (props.column.field === 'issue_category'
                                    ? props.formattedRow.issue_category.label
                                    : props.formattedRow[props.column.field])
                        }}
                    </span>
                </template>
            </vue-good-table>
            </div>
        </SfModal>
    </div>
</template>

<script>
import {
    defineComponent,
    ref,
    computed
} from '@nuxtjs/composition-api';
import { SfInput, SfModal, SfLoader } from '@storefront-ui/vue';
import { ValidationProvider, ValidationObserver, extend } from 'vee-validate';
import { email, min } from 'vee-validate/dist/rules';
import { format } from 'date-fns';
import { VueGoodTable } from 'vue-good-table';

extend('email', {
	...email,
	message: 'The email field must be a valid email',
});

extend('min', {
  ...min,
  message: 'This field have more than 3 characters',
});

export default defineComponent({
    name: 'Step3',
    components: {
		SfLoader,
        SfInput,
        SfModal,
        ValidationProvider,
        ValidationObserver,
        VueGoodTable
	},
    props: {
        applicantDetails: Object,
        applicationSubmitted: Boolean,
        showFinalStepWarning: Boolean,
        orders: Array,
	},
    setup(props, { emit }) {
        const saveApplicantDetails = () => {
            emit('saveApplicantDetails', props.applicantDetails);
        };

        const luminaireColumns = ref([
            { label: 'Article', field: 'article' },
            { label: 'Quantity', field: 'quantity' },
            { label: 'Preferred course of action', field: 'preferredCourseOfAction',width: '190px' },
            { label: 'End user', field: 'endUser' },
            { label: 'Warranty', field: 'warranty' },
            { label: 'Warranty case', field: 'warranty_case' },
            { label: 'Model number', field: 'model_number' },
            { label: 'Order delivery date', field: 'order_delivery_date' },
            { label: 'Issues', field: 'issues' },
        ]);
        const isModalOpen = ref(false);
        const activeIssues = ref([]);

        const openIssuesModal = (row) => {
            activeIssues.value = row.issues;
            isModalOpen.value = true;
        };

        const issueColumnsData = ref([
            { label: 'Issue category', field: 'issue_category' },
            { label: 'Quantity', field: 'quantity' },
            { label: 'Installation date', field: 'installation_date' },
            { label: 'Failure date', field: 'failure_date' },
            { label: 'Description', field: 'description' },
        ]);

        const groupedClaims = computed(() => {
            return props.orders.reduce((acc, order) => {
                const key = order.sales_order_number;
                if (!acc[key]) acc[key] = [];
                acc[key].push(order);
                return acc;
            }, {});
        });

        const formatDate = (date) => {
            return date ? format(new Date(date), "MMMM d, yyyy") : "";
        };

        const formatLabel = (key) => {
            return key
                .replace(/_/g, ' ')
                .replace(/\b\w/g, l => l.toUpperCase());
        }

        const formatValue = (value) => {
            if (typeof value === 'object') {
                return value?.label ?? '[object]';
            }
            return value;
        }

        const isDateField = (fieldName) => {
            return fieldName.toLowerCase().includes("date");
        };

        return {
            saveApplicantDetails,
            groupedClaims,
            formatLabel,
            formatValue,
            formatDate,
            luminaireColumns,
            issueColumnsData,
            openIssuesModal,
            isDateField,
            activeIssues,
            isModalOpen,
        };
    },
});
</script>

<style lang="scss" scoped>
    .order-attributes {
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        width: 100%;
        align-items: flex-start;

        @media (min-width: 998px) {
            flex-wrap: nowrap;
        }

        div {
            flex-grow: 1;
            width: 100%;
        }
    }

    .half-width {
        .form-group {
            gap: 1rem; /* Adjust spacing between inputs */
            width: 40%;
        }
    }

    .form__element {
        flex: 1; /* Makes inputs take equal width */
    }

    .warning-message {
        color: var(--_c-red-primary);
        min-height: 30px;
    }

    ::v-deep .sf-modal__content {
        padding: 2rem 4rem;
    }

    .main-attributes{
        border-radius: 0.5rem;
        display: flex;
        flex-direction: column;

        .flex-between {
            width: 40%;
            text-align: left;

            div {
                width: 100%;
            }
        }
    }

    .flex {
        flex-wrap: wrap;
    }

    ::v-deep .vgt-wrap {
        background-color: var(--c-white-blue);
    }

    .step-3 {
        ::v-deep .sf-modal__container {
            width: 100% !important;

            @media (min-width: 800px) {
                width: 760px !important;
            }
        }
    }
</style>